import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Upload / empty / failed intake state + "pull from a recorded meeting" (Zoom / Fireflies).
// Owns its own intake state; hands the started analysis back up via onStarted(payload).
export function CaseIntake({ clientId, failed, errorMsg, onBack, onStarted }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState(null);
  const [pullOpen, setPullOpen] = useState(null); // 'zoom' | 'fireflies' | null
  const [pullItems, setPullItems] = useState([]);
  const [pullLoading, setPullLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/meeting-transcripts/providers").then(({ data }) => setProviders(data)).catch(() => setProviders({ zoom: false, fireflies: false }));
  }, []);

  const startAnalysis = async (fileObj) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (fileObj) fd.append("file", fileObj);
      if (text.trim()) fd.append("transcript_text", text.trim());
      fd.append("source", fileObj ? "upload" : "manual");
      const { data: d } = await api.post(`/case-analysis/${clientId}/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allison is analyzing the transcript…");
      onStarted({ exists: true, status: d.status });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start analysis"); }
    finally { setSubmitting(false); }
  };

  const openPull = async (provider) => {
    if (pullOpen === provider) { setPullOpen(null); return; }
    setPullOpen(provider); setPullLoading(true); setPullItems([]);
    try {
      const url = provider === "zoom" ? `/meeting-transcripts/zoom/for-client/${clientId}` : `/meeting-transcripts/fireflies/recent`;
      const { data } = await api.get(url);
      setPullItems(data.items || []);
    } catch { toast.error(`Couldn't load ${provider} meetings.`); }
    finally { setPullLoading(false); }
  };

  const importMeeting = async (provider, item) => {
    const id = provider === "zoom" ? item.meeting_id : item.id;
    setImportingId(id);
    try {
      if (provider === "zoom") await api.post(`/meeting-transcripts/zoom/import`, { client_id: clientId, meeting_id: item.meeting_id });
      else await api.post(`/meeting-transcripts/fireflies/import/${item.id}`, { client_id: clientId });
      toast.success("Transcript imported — Allison is analyzing it…");
      setPullOpen(null);
      onStarted({ exists: true, status: "processing" });
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImportingId(null); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="ca-upload">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4"><Icons.ArrowLeft className="h-4 w-4" />Back to workspace</button>
      <div className="flex items-center gap-2 mb-1"><Icons.Brain className="h-6 w-6 text-blue-600" /><h1 className="text-2xl font-bold text-slate-900">AI Case Analysis</h1></div>
      <p className="text-slate-500 mb-6">Upload a meeting transcript and Allison will build the complete engagement blueprint — services, pricing, missing documents, risks, and a ready-to-send estimate.</p>
      {failed && <div className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-4 py-2" data-testid="ca-error">{errorMsg || "Analysis failed. Please try again."}</div>}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="ca-transcript-text"
          placeholder="Paste the meeting transcript here…" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        <div className="flex items-center gap-3 mt-3">
          <button onClick={() => startAnalysis(null)} disabled={submitting || !text.trim()} data-testid="ca-analyze-text"
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50">
            {submitting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}Analyze Transcript
          </button>
          <span className="text-xs text-slate-400">or</span>
          <button onClick={() => fileRef.current?.click()} disabled={submitting} data-testid="ca-upload-file"
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg px-4 py-2">
            <Icons.Upload className="h-4 w-4" />Upload file (.txt .vtt .docx .pdf or audio)
          </button>
          <input ref={fileRef} type="file" hidden accept=".txt,.vtt,.docx,.pdf,.mp3,.mp4,.m4a,.wav,.webm,.mpeg,.mpga" onChange={(e) => { const f = e.target.files?.[0]; if (f) startAnalysis(f); e.target.value = ""; }} />
        </div>
      </div>

      {providers && (providers.zoom || providers.fireflies) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4" data-testid="ca-pull-panel">
          <div className="flex items-center gap-2 mb-1"><Icons.Video className="h-4 w-4 text-indigo-600" /><h3 className="text-sm font-semibold text-slate-800">Pull from a recorded meeting</h3></div>
          <p className="text-xs text-slate-400 mb-3">Import a transcript straight from your meeting tools — Allison analyzes it automatically.</p>
          <div className="flex items-center gap-3">
            {providers.zoom && (
              <button onClick={() => openPull("zoom")} data-testid="ca-pull-zoom"
                className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-lg px-4 py-2 border ${pullOpen === "zoom" ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                <Icons.Video className="h-4 w-4" />Pull from Zoom
              </button>
            )}
            {providers.fireflies && (
              <button onClick={() => openPull("fireflies")} data-testid="ca-pull-fireflies"
                className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-lg px-4 py-2 border ${pullOpen === "fireflies" ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                <Icons.Sparkles className="h-4 w-4" />Pull from Fireflies
              </button>
            )}
          </div>
          {pullOpen && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="ca-pull-list">
              {pullLoading ? (
                <div className="text-sm text-slate-400 flex items-center gap-2 py-3"><Icons.Loader2 className="h-4 w-4 animate-spin" />Loading meetings…</div>
              ) : pullItems.length === 0 ? (
                <div className="text-sm text-slate-400 py-3">{pullOpen === "zoom" ? "No Zoom meetings with a recording found for this client yet." : "No recent Fireflies transcripts found."}</div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {pullItems.map((it) => {
                    const id = pullOpen === "zoom" ? it.meeting_id : it.id;
                    return (
                      <div key={id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`ca-pull-item-${id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{it.topic || it.title || "Meeting"}</div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {it.start_time ? new Date(it.start_time).toLocaleString() : it.date ? new Date(Number(it.date) || it.date).toLocaleString() : ""}
                            {it.participants?.length ? ` · ${it.participants.slice(0, 3).join(", ")}` : ""}
                          </div>
                        </div>
                        <button onClick={() => importMeeting(pullOpen, it)} disabled={importingId === id} data-testid={`ca-import-${id}`}
                          className="shrink-0 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg px-3 py-1.5 disabled:opacity-50">
                          {importingId === id ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Download className="h-3.5 w-3.5" />}Import & analyze
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
